import { ReactNode, useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Field, Form, Formik } from 'formik';
import { object, string } from 'yup';

import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Icon } from '@@/Icon';
import { FormError } from '@@/form-components/FormError';

const schema = object({
  username: string().trim().required('Username is required'),
  password: string().required('Password is required'),
});

export function LoginForm({
  onSubmit,
  isLoading = false,
  initialValues = { username: '', password: '' },
}: {
  onSubmit(values: { username: string; password: string }): void;
  isLoading?: boolean;
  initialValues?: { username: string; password: string };
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Formik
      onSubmit={(values) => {
        setShowPassword(false);
        onSubmit({ ...values, username: values.username.trim() });
      }}
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ errors, touched }) => (
        <Form className="simple-box-form form-horizontal login-form">
          <Control
            label="Username"
            inputId="username-field"
            error={touched.username ? errors.username : undefined}
            field={
              <Field
                as={Input}
                id="username-field"
                data-cy="auth-usernameInput"
                className="form-control"
                name="username"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                placeholder="Enter your username"
              />
            }
          />

          <Control
            label="Password"
            inputId="password-field"
            error={touched.password ? errors.password : undefined}
            field={
              <PasswordField
                id="password-field"
                showPassword={showPassword}
                onToggle={() => setShowPassword((value) => !value)}
              />
            }
          />

          <LoadingButton
            isLoading={isLoading}
            data-cy="auth-loginButton"
            loadingText="Login in progress..."
            size="large"
            className="w-full"
          >
            Login
          </LoadingButton>
        </Form>
      )}
    </Formik>
  );
}

function PasswordField({
  id,
  showPassword,
  onToggle,
}: {
  id: string;
  showPassword: boolean;
  onToggle(): void;
}) {
  const toggleLabel = showPassword ? 'Hide password' : 'Show password';

  return (
    <div className="relative">
      <Field
        as={Input}
        id={id}
        className="form-control pr-10"
        name="password"
        autoComplete="off"
        data-cy="auth-passwordInput"
        placeholder="Enter your password"
        type={showPassword ? 'text' : 'password'}
      />
      <TooltipWithChildren message={toggleLabel} position="top">
        <button
          aria-label={toggleLabel}
          data-cy="auth-passwordInputToggle"
          type="button"
          onClick={onToggle}
          className="absolute top-0 right-0 flex h-[34px] w-[50px] items-center justify-center border-none bg-transparent"
        >
          <Icon icon={showPassword ? EyeIcon : EyeOffIcon} size="md" />
        </button>
      </TooltipWithChildren>
    </div>
  );
}

export function Control({
  label,
  field,
  inputId,
  error,
}: {
  label: string;
  field: ReactNode;
  inputId: string;
  error?: string;
}) {
  return (
    <div>
      <label className="m-0 pb-2 font-normal" htmlFor={inputId}>
        {label}
      </label>
      {field}
      {!!error && <FormError>{error}</FormError>}
    </div>
  );
}
